/**
 * ReportDownloadSettingsCard
 * ──────────────────────────
 * Admin control panel for the CSE annual/interim report auto-downloader.
 *
 * Endpoints (see cse_reports_service.py + server.py):
 *   GET /api/admin/settings/report-download   → current policy
 *   PUT /api/admin/settings/report-download   → partial update
 *
 * Fields:
 *   report_types               ["annual"] | subset of {annual, interim, quarterly, other}
 *   auto_analyse_on_download   bool — when true, newly downloaded PDFs are
 *                              queued for Claude analysis in the SAME sweep
 *                              (default OFF to stop a discovery run racking
 *                              up a 200+ report Claude bill overnight).
 *   years_to_keep              int — retention hint (used later by the
 *                              future historical backfill job).
 *
 * UX notes:
 *   • The card renders even when the API response is still loading so the
 *     page layout doesn't jump. Skeleton checkboxes are inert.
 *   • Saving is optimistic — we flip local state first, fire PUT, then
 *     reconcile with the server's normalised response. Errors revert.
 *   • Nightly cron behaviour is explained inline so admins understand the
 *     implication of turning auto-analyse OFF (top-20 annual BUYs still get
 *     processed at 20:00 SLT).
 *   • Deleting the last remaining report_type is blocked client-side AND
 *     server-side (400 on the PUT). Guarding here avoids the round-trip.
 */
import React, { useEffect, useState } from "react";
import { apiGet, api } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, Loader2, Save, Sparkles, Info } from "lucide-react";
import { toast } from "sonner";

const REPORT_TYPE_CHOICES = [
  {
    key: "annual",
    label: "Annual Reports",
    hint: "Most analytical value — full financials, MD&A, risk factors.",
    recommended: true,
  },
  {
    key: "interim",
    label: "Interim Reports",
    hint: "Half-year filings. Useful for trend-checks mid-year.",
  },
  {
    key: "quarterly",
    label: "Quarterly Results",
    hint: "Raw numbers only, light on commentary.",
  },
  {
    key: "other",
    label: "Other Announcements",
    hint: "Corporate actions / press releases. Noisy — usually skip.",
  },
];

const DEFAULT_STATE = {
  report_types: ["annual"],
  auto_analyse_on_download: false,
  years_to_keep: 5,
};

const ReportDownloadSettingsCard = () => {
  const [settings, setSettings] = useState(DEFAULT_STATE);
  const [initial, setInitial] = useState(DEFAULT_STATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet("/admin/settings/report-download");
        if (cancelled) return;
        const normalised = {
          report_types: Array.isArray(res?.report_types) && res.report_types.length > 0
            ? res.report_types
            : ["annual"],
          auto_analyse_on_download: !!res?.auto_analyse_on_download,
          years_to_keep: res?.years_to_keep || 5,
        };
        setSettings(normalised);
        setInitial(normalised);
      } catch {
        // Leave defaults; card still renders + saves work once backend reachable.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dirty = JSON.stringify(settings) !== JSON.stringify(initial);

  const toggleType = (key, checked) => {
    setSettings((prev) => {
      const types = new Set(prev.report_types);
      if (checked) types.add(key);
      else types.delete(key);
      if (types.size === 0) {
        // Client-side guard — keep the clicked item on, show toast.
        toast.error("At least one report type must remain selected.");
        return prev;
      }
      return { ...prev, report_types: Array.from(types) };
    });
  };

  const save = async () => {
    if (settings.report_types.length === 0) {
      toast.error("Pick at least one report type.");
      return;
    }
    setSaving(true);
    try {
      const res = await api.put("/admin/settings/report-download", settings);
      const next = {
        report_types: res?.data?.report_types || settings.report_types,
        auto_analyse_on_download: !!res?.data?.auto_analyse_on_download,
        years_to_keep: res?.data?.years_to_keep || 5,
      };
      setSettings(next);
      setInitial(next);
      toast.success("Auto-download settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border bg-card" data-testid="report-download-settings-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-display flex items-center gap-2 text-base">
          <Download className="h-4 w-4 text-primary" />
          Auto-Download Settings
        </CardTitle>
        <CardDescription className="text-[12px]">
          Controls the nightly CSE report discovery job (runs 18:30 SLT).
          Changes take effect on the next run.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* --------- Report types --------- */}
        <div>
          <Label className="text-xs font-semibold">Report types to download</Label>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {REPORT_TYPE_CHOICES.map((choice) => {
              const checked = settings.report_types.includes(choice.key);
              return (
                <label
                  key={choice.key}
                  htmlFor={`report-type-${choice.key}`}
                  className={`flex items-start gap-2 rounded-md border p-2.5 cursor-pointer transition-colors ${
                    checked
                      ? "border-primary/40 bg-primary/5"
                      : "border-border hover:bg-muted/50"
                  }`}
                  data-testid={`report-type-row-${choice.key}`}
                >
                  <Checkbox
                    id={`report-type-${choice.key}`}
                    checked={checked}
                    disabled={loading}
                    onCheckedChange={(v) => toggleType(choice.key, !!v)}
                    className="mt-0.5"
                    data-testid={`report-type-checkbox-${choice.key}`}
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium flex items-center gap-1.5">
                      {choice.label}
                      {choice.recommended && (
                        <span className="text-[9px] font-semibold uppercase tracking-wider px-1 py-0 rounded bg-primary/10 text-primary">
                          recommended
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                      {choice.hint}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* --------- Years to keep --------- */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label className="text-xs font-semibold">Years to keep</Label>
            <div className="text-[11px] text-muted-foreground leading-snug">
              Used by the future historical backfill job — newer is always kept.
            </div>
          </div>
          <Select
            value={String(settings.years_to_keep)}
            onValueChange={(v) => setSettings((p) => ({ ...p, years_to_keep: parseInt(v, 10) }))}
            disabled={loading}
          >
            <SelectTrigger
              className="w-[180px]"
              data-testid="years-to-keep-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[3, 5, 7, 10, 15].map((y) => (
                <SelectItem key={y} value={String(y)} data-testid={`years-to-keep-${y}`}>
                  Last {y} years
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* --------- Auto-analyse toggle --------- */}
        <div className="rounded-md border border-dashed bg-muted/30 p-3 space-y-2">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                Auto-analyse after download
              </div>
              <div className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                When OFF (recommended) freshly downloaded reports wait for the
                20:00 SLT cron, which analyses the <strong>top 20 annual reports</strong>{" "}
                sorted by composite score (highest BUY stocks first). When ON,
                every new download triggers Claude immediately — can be expensive.
              </div>
            </div>
            <Switch
              checked={settings.auto_analyse_on_download}
              disabled={loading}
              onCheckedChange={(v) =>
                setSettings((p) => ({ ...p, auto_analyse_on_download: !!v }))
              }
              data-testid="auto-analyse-switch"
            />
          </div>
          <div
            className={`rounded-md px-2.5 py-1.5 text-[11px] flex items-start gap-1.5 ${
              settings.auto_analyse_on_download
                ? "bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30"
                : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30"
            }`}
            data-testid="auto-analyse-impact-banner"
          >
            <Info className="h-3 w-3 shrink-0 mt-0.5" />
            {settings.auto_analyse_on_download ? (
              <span>
                <strong>Aggressive mode:</strong> every new download will burn
                Emergent LLM budget immediately. Nightly cron runs regardless.
              </span>
            ) : (
              <span>
                <strong>Controlled mode:</strong> downloads wait in the pending
                queue. Nightly cron analyses top-20 annual BUY stocks at 20:00 SLT.
                Admin can trigger per-report analysis manually from the stock page.
              </span>
            )}
          </div>
        </div>

        {/* --------- Save --------- */}
        <div className="flex items-center justify-between gap-2 pt-1">
          <div className="text-[11px] text-muted-foreground">
            {dirty ? "Unsaved changes" : "All changes saved"}
          </div>
          <Button
            size="sm"
            variant="default"
            onClick={save}
            disabled={!dirty || saving || loading}
            className="gap-1.5 min-w-[120px] justify-center"
            data-testid="save-report-download-settings-btn"
          >
            {saving ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save className="h-3.5 w-3.5" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReportDownloadSettingsCard;
